/**
 * TEND in-app agent — the panel's state (phase 1; phase 3 adds proposals,
 * the fallback row, the budget and auto-confirm).
 *
 * One provider at App level (the state survives trio navigation); the dock
 * (AgentDock) mounts inside the brand shell and calls loadBrand(slug). The
 * transcript is rebuilt from the thread on load and appended to as a turn
 * streams; drafts are the thread's pending/decided items, keyed by id;
 * actions are the proposal cards (queue / take), keyed by id. Deciding a
 * card appends the outcome to the thread server-side and the panel resumes
 * the turn so the agent can carry on.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import agentApi, {
  AgentApiError,
  AgentDraft,
  AgentEvent,
  AgentSpeed,
  AgentStatus,
  AgentThreadDetail,
  AgentThreadSummary,
  PendingAction,
} from '../api/agentApi';
import { AGENT_MAX_COLS, AGENT_MIN_COLS, AGENT_REFRESH_EVENT } from '../utils/agentPush';
import { loadBrandPalette } from './BrandPaletteContext';

/** Fired after the agent's Colors card is approved: Design ▸ Colors re-reads the roles. */
export const BRAND_COLORS_CHANGED_EVENT = 'tend:brand-colors-changed';

export type AgentEntry =
  | { kind: 'user'; id: string; text: string }
  | { kind: 'tend'; id: string; text: string; streaming?: boolean }
  | { kind: 'act'; id: string; name: string; state: 'run' | 'ok' | 'err'; summary?: string; startedAt?: number; endedAt?: number }
  | { kind: 'thinking'; id: string; startedAt: number; endedAt?: number }
  | { kind: 'drafts'; id: string; ids: number[] }
  | { kind: 'action'; id: string; actionId: number }
  | { kind: 'fallback'; id: string; from: string; to: string; reason: string }
  | { kind: 'budget'; id: string; level: 'warning' | 'exhausted'; spent: number; limit: number; who: string }
  | { kind: 'error'; id: string; message: string }
  | { kind: 'note'; id: string; text: string };

export interface AgentBudget {
  limit: number;
  spent: number;
  warnAt: number;
  /** The publishing timezone the spend day is counted in (the same for every brand). */
  timezone?: string;
}

export interface AgentState {
  slug: string | null;
  brandId: number | null;
  status: AgentStatus | null;
  available: boolean;
  canManage: boolean;
  loading: boolean;
  open: boolean;
  busy: boolean;
  cols: number;
  speed: AgentSpeed | null;
  effectiveSpeed: AgentSpeed;
  thread: AgentThreadSummary | null;
  readOnly: boolean;
  entries: AgentEntry[];
  drafts: Record<number, AgentDraft>;
  actions: Record<number, PendingAction>;
  budget: AgentBudget | null;
  autoConfirmBrand: boolean;
  history: AgentThreadSummary[];
  historyOpen: boolean;
  composerSeed: string | null;
  lastError: string | null;
}

export interface AgentActions {
  loadBrand: (slug: string) => void;
  openPanel: () => void;
  closePanel: () => void;
  togglePanel: () => void;
  send: (text: string) => Promise<void>;
  stop: () => void;
  confirm: (id: number) => Promise<void>;
  skip: (id: number) => Promise<void>;
  confirmAll: () => Promise<void>;
  /** Phase 3: confirm every draft and auto-confirm later ones in this thread. */
  confirmAllStopAsking: () => Promise<void>;
  setAutoConfirm: (on: boolean) => Promise<void>;
  skipAll: () => Promise<void>;
  revise: (id: number) => void;
  seedComposer: (text: string | null) => void;
  newThread: () => Promise<void>;
  openHistory: (on: boolean) => void;
  viewThread: (id: number) => Promise<void>;
  continueThread: () => void;
  refreshDrafts: () => Promise<void>;
  /** Phase 2b: render a draft's payload again. */
  renderAgain: (id: number) => Promise<void>;
  /** Phase 3: decide a proposal card, then resume the turn on the outcome. */
  approveAction: (id: number, stopAsking?: boolean) => Promise<void>;
  skipAction: (id: number) => Promise<void>;
  setSpeed: (speed: AgentSpeed) => Promise<void>;
  setCols: (cols: number) => void;
  setPageContext: (text: string | null) => void;
  addNote: (text: string) => void;
}

export type AgentContextType = AgentState & AgentActions;

const AgentContext = createContext<AgentContextType | undefined>(undefined);

export const useAgent = (): AgentContextType => {
  const ctx = useContext(AgentContext);
  if (!ctx) throw new Error('useAgent must be used within an AgentProvider');
  return ctx;
};

const colsKey = (slug: string) => `tend-agent-cols:${slug}`;
const readCols = (slug: string): number | null => {
  try {
    const raw = localStorage.getItem(colsKey(slug));
    const n = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(n) ? Math.max(AGENT_MIN_COLS, Math.min(AGENT_MAX_COLS, n)) : null;
  } catch (e) { return null; }
};

let entrySeq = 0;
const eid = () => `e${Date.now().toString(36)}${(entrySeq++).toString(36)}`;

/** A person's decision on a card is stored as their next message; it reads as a note, not a bubble. */
const DECISION_RE = /^\[(Approved|Skipped)\]/;
const BRIEF_RE = /^Auto Tend run:/;      // the sweep's first message, carried into a picked-up thread
const PICKED_UP_RE = /^Picked up by /;   // the seam note a pick-up adds
const RENDER_BRIEF_RE = /^The render for draft /;   // Agent eyes: the brief that opens the render's follow-up turn

const whoSpent = (by: { name: string; spent: number }[], auto: number): string => {
  const parts = by.map((p) => `${p.name} $${p.spent.toFixed(2)}`);
  if (auto > 0) parts.push(`auto runs $${auto.toFixed(2)}`);
  return parts.join(', ');
};

/** Rebuild the panel's transcript from a stored thread (reasoning never shown). */
export function entriesFromDetail(detail: AgentThreadDetail): AgentEntry[] {
  const results = new Map<string, boolean>();
  detail.messages.forEach((m) => m.blocks.forEach((b) => { if (b.type === 'tool_result') results.set(b.tool_use_id, b.ok); }));
  const out: AgentEntry[] = [];
  const isAuto = detail.thread.kind === 'auto';
  detail.messages.forEach((m) => {
    if (m.role === 'user') {
      m.blocks.forEach((b) => {
        if (b.type !== 'text' || !b.text.trim()) return;
        // An Auto Tend run's first message is the sweep's brief, not a person's words.
        if (DECISION_RE.test(b.text) || isAuto || BRIEF_RE.test(b.text)) out.push({ kind: 'note', id: eid(), text: b.text });
        else out.push({ kind: 'user', id: eid(), text: b.text });
      });
    } else if (m.role === 'assistant') {
      if (m.fallback_from && m.model) out.push({ kind: 'fallback', id: eid(), from: m.fallback_from, to: m.model, reason: 'unavailable' });
      m.blocks.forEach((b) => {
        if (b.type === 'text' && b.text.trim()) out.push({ kind: 'tend', id: eid(), text: b.text });
        if (b.type === 'tool_use') {
          const ok = results.get(b.id);
          out.push({ kind: 'act', id: b.id, name: b.name, state: ok === undefined ? 'run' : ok ? 'ok' : 'err' });
        }
      });
    } else if (m.role === 'system') {
      // Page context stays private; the pick-up seam shows, so the transcript says where the person came in.
      m.blocks.forEach((b) => {
        if (b.type === 'text' && (PICKED_UP_RE.test(b.text) || RENDER_BRIEF_RE.test(b.text))) out.push({ kind: 'note', id: eid(), text: b.text });
      });
    }
  });
  (detail.pending_actions || []).forEach((a) => out.push({ kind: 'action', id: `action-${a.id}`, actionId: a.id }));
  if (detail.drafts.length) out.push({ kind: 'drafts', id: eid(), ids: detail.drafts.map((d) => d.id) });
  return out;
}

const initial: AgentState = {
  slug: null, brandId: null, status: null, available: false, canManage: false, loading: false,
  open: false, busy: false, cols: 2, speed: null, effectiveSpeed: 'balanced',
  thread: null, readOnly: false, entries: [], drafts: {}, actions: {}, budget: null, autoConfirmBrand: false,
  history: [], historyOpen: false, composerSeed: null, lastError: null,
};

const friendly = (e: unknown): string => {
  if (e instanceof AgentApiError) return e.message;
  if (e instanceof Error) return e.message || 'Something went wrong';
  return 'Something went wrong';
};

export const AgentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AgentState>(initial);
  const pageContextRef = useRef<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const slugRef = useRef<string | null>(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  const patch = useCallback((p: Partial<AgentState> | ((s: AgentState) => Partial<AgentState>)) => {
    setState((s) => ({ ...s, ...(typeof p === 'function' ? p(s) : p) }));
  }, []);

  const addEntry = useCallback((entry: AgentEntry) => {
    setState((s) => ({ ...s, entries: [...s.entries, entry] }));
  }, []);

  const addNote = useCallback((text: string) => addEntry({ kind: 'note', id: eid(), text }), [addEntry]);

  const indexDetail = (detail: AgentThreadDetail) => {
    const drafts: Record<number, AgentDraft> = {};
    detail.drafts.forEach((d) => { drafts[d.id] = d; });
    const actions: Record<number, PendingAction> = {};
    (detail.pending_actions || []).forEach((a) => { actions[a.id] = a; });
    return { drafts, actions };
  };

  const loadThreadDetail = useCallback(async (threadId: number, readOnly: boolean) => {
    const detail = await agentApi.getThread(threadId);
    patch({ thread: detail.thread, entries: entriesFromDetail(detail), ...indexDetail(detail), readOnly, historyOpen: false });
  }, [patch]);

  const loadBrand = useCallback((slug: string) => {
    if (slugRef.current === slug) return;
    slugRef.current = slug;
    abortRef.current?.abort();
    const stored = readCols(slug);
    patch({ ...initial, slug, loading: true, cols: stored ?? 2 });
    (async () => {
      try {
        const status = await agentApi.status(slug);
        if (slugRef.current !== slug) return;
        const prefs = status.prefs;
        const cols = prefs?.panel_cols ?? stored ?? 2;
        patch({
          status, brandId: status.brand_id ?? null, available: !!status.available, canManage: !!status.can_manage,
          speed: prefs?.speed ?? null, effectiveSpeed: prefs?.effective_speed ?? status.default_speed ?? 'balanced',
          cols,
          budget: status.budget ? { limit: status.budget.limit, spent: status.budget.spent_today, warnAt: status.budget.warn_at, timezone: status.budget.timezone } : null,
          autoConfirmBrand: !!(status.auto_confirm && status.auto_confirm.drafts),
        });
        if (!status.available) { patch({ loading: false }); return; }
        const thread = await agentApi.activeThread(slug);
        if (slugRef.current !== slug) return;
        await loadThreadDetail(thread.id, false);
        patch({ loading: false });
      } catch (e) {
        if (slugRef.current !== slug) return;
        patch({ loading: false, available: false, lastError: friendly(e) });
      }
    })();
  }, [patch, loadThreadDetail]);

  const openPanel = useCallback(() => patch({ open: true }), [patch]);
  const closePanel = useCallback(() => patch({ open: false, historyOpen: false }), [patch]);
  const togglePanel = useCallback(() => patch((s) => ({ open: !s.open, historyOpen: false })), [patch]);
  const openHistory = useCallback((on: boolean) => patch({ historyOpen: on }), [patch]);
  const seedComposer = useCallback((text: string | null) => patch({ composerSeed: text }), [patch]);
  const setPageContext = useCallback((text: string | null) => { pageContextRef.current = text; }, []);

  const refreshHistory = useCallback(async () => {
    const slug = slugRef.current;
    if (!slug) return;
    try {
      const { threads } = await agentApi.listThreads(slug);
      patch({ history: threads });
    } catch (e) { /* the drawer shows what it has */ }
  }, [patch]);

  const refreshThread = useCallback(async () => {
    const id = stateRef.current.thread?.id;
    if (!id) return;
    try {
      const detail = await agentApi.getThread(id);
      // Between turns the stored transcript is the truth (a render's follow-up turn lands there);
      // while a turn streams here, the entries stay the stream's.
      patch(stateRef.current.busy
        ? { thread: detail.thread, ...indexDetail(detail) }
        : { thread: detail.thread, entries: entriesFromDetail(detail), ...indexDetail(detail) });
    } catch (e) { /* keep what we have */ }
  }, [patch]);

  // Phase 2b: while a draft's payload is rendering, re-read the thread every few
  // seconds so the card swaps 'Rendering' for the video when the files land.
  // …and while the render's follow-up turn (Agent eyes) is pending or running, so its messages arrive too.
  const rendering = Object.values(state.drafts).some((d) => d.render && (
    d.render.status === 'queued' || d.render.status === 'rendering'
    || d.render.check_turn === 'pending' || d.render.check_turn === 'running'));
  useEffect(() => {
    if (!rendering) return undefined;
    const handle = window.setInterval(() => { void refreshThread(); }, 5000);
    return () => window.clearInterval(handle);
  }, [rendering, refreshThread]);

  const announceRefresh = () => { try { window.dispatchEvent(new CustomEvent(AGENT_REFRESH_EVENT)); } catch (e) { /* noop */ } };

  /** One streamed turn: a new message, or a resume on the transcript after a card was decided. */
  const runTurn = useCallback(async (threadId: number, body: { text?: string; resume?: boolean }) => {
    const current = stateRef.current;
    const controller = new AbortController();
    abortRef.current = controller;
    patch({ busy: true, readOnly: false, composerSeed: null, lastError: null, historyOpen: false });
    const speedOverride = current.speed && current.thread?.speed && current.speed !== current.thread.speed ? current.speed : undefined;
    let gridChanged = false;
    const apply = (ev: AgentEvent) => {
      setState((s) => {
        const entries = [...s.entries];
        const last = entries[entries.length - 1];
        const closeStream = () => { if (last && last.kind === 'tend' && last.streaming) entries[entries.length - 1] = { ...last, streaming: false }; };
        // the model's reasoning row stops its clock at the next thing that happens
        const closeThinking = () => {
          for (let i = entries.length - 1; i >= 0; i -= 1) {
            const x = entries[i];
            if (x.kind === 'thinking') { if (!x.endedAt) entries[i] = { ...x, endedAt: Date.now() }; break; }
          }
        };
        if (ev.event !== 'thinking' && ev.event !== 'turn' && ev.event !== 'waiting') closeThinking();
        switch (ev.event) {
          case 'thinking':
            closeStream();
            closeThinking();
            entries.push({ kind: 'thinking', id: eid(), startedAt: Date.now() });
            return { ...s, entries };
          case 'text': {
            if (last && last.kind === 'tend' && last.streaming) {
              entries[entries.length - 1] = { ...last, text: last.text + ev.delta };
            } else {
              entries.push({ kind: 'tend', id: eid(), text: ev.delta, streaming: true });
            }
            return { ...s, entries };
          }
          case 'waiting':
            // another turn holds this thread (a second tab, the render's follow-up); this one lands after it
            closeStream();
            entries.push({ kind: 'note', id: eid(), text: ev.message });
            return { ...s, entries };
          case 'tool_start':
            closeStream();
            entries.push({ kind: 'act', id: ev.id, name: ev.name, state: 'run', startedAt: Date.now() });
            return { ...s, entries };
          case 'tool_result': {
            const idx = entries.findIndex((e) => e.kind === 'act' && e.id === ev.id);
            if (idx >= 0) { const was = entries[idx]; entries[idx] = { kind: 'act', id: ev.id, name: ev.name, state: ev.ok ? 'ok' : 'err', summary: ev.summary, startedAt: was.kind === 'act' ? was.startedAt : undefined, endedAt: Date.now() }; }
            return { ...s, entries };
          }
          case 'needs_approval': {
            closeStream();
            entries.push({ kind: 'action', id: `action-${ev.action.id}`, actionId: ev.action.id });
            const t = s.thread ? { ...s.thread, open_actions: (s.thread.open_actions || 0) + 1 } : s.thread;
            return { ...s, entries, actions: { ...s.actions, [ev.action.id]: ev.action }, thread: t };
          }
          case 'fallback':
            closeStream();
            entries.push({ kind: 'fallback', id: eid(), from: ev.from, to: ev.to, reason: ev.reason });
            return { ...s, entries };
          case 'budget':
            closeStream();
            entries.push({ kind: 'budget', id: eid(), level: ev.level, spent: ev.spent, limit: ev.limit, who: whoSpent(ev.by_user || [], ev.auto || 0) });
            return { ...s, entries, budget: { limit: ev.limit, spent: ev.spent, warnAt: s.budget?.warnAt ?? 0.8, timezone: s.budget?.timezone } };
          case 'drafts': {
            closeStream();
            const drafts = { ...s.drafts };
            ev.items.forEach((d) => { drafts[d.id] = d; });
            entries.push({ kind: 'drafts', id: eid(), ids: ev.items.map((d) => d.id) });
            if (ev.auto_confirmed) {
              gridChanged = true;
              entries.push({ kind: 'note', id: eid(), text: `${ev.items.length === 1 ? 'Confirmed on arrival' : `${ev.items.length} confirmed on arrival`} · auto-confirm is on.` });
            }
            return { ...s, entries, drafts };
          }
          case 'error':
            closeStream();
            entries.push({ kind: 'error', id: eid(), message: ev.message });
            return { ...s, entries };
          case 'done': {
            closeStream();
            const t = s.thread ? { ...s.thread, pending_count: ev.pending_count, open_actions: ev.open_actions ?? s.thread.open_actions } : s.thread;
            const budget = ev.budget ? { limit: ev.budget.limit, spent: ev.budget.spent_today, warnAt: s.budget?.warnAt ?? 0.8, timezone: s.budget?.timezone } : s.budget;
            return { ...s, entries, thread: t, budget };
          }
          case 'turn':
            return s.thread ? { ...s, thread: { ...s.thread, model_at_start: s.thread.model_at_start || ev.model } } : s;
          case 'usage':
            // each model call's price lands on the thread as it happens (the server adds the same number to the row)
            return s.thread ? { ...s, thread: { ...s.thread, cost_usd: (s.thread.cost_usd || 0) + (ev.cost_usd || 0) } } : s;
          default:
            return s;
        }
      });
    };
    try {
      await agentApi.streamTurn(threadId, { ...body, page_context: pageContextRef.current, speed: speedOverride }, apply, controller.signal);
    } catch (e) {
      if (!(e instanceof DOMException && e.name === 'AbortError')) addEntry({ kind: 'error', id: eid(), message: friendly(e) });
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
      setState((s) => {
        const entries = [...s.entries];
        const last = entries[entries.length - 1];
        if (last && last.kind === 'tend' && last.streaming) entries[entries.length - 1] = { ...last, streaming: false };
        return { ...s, busy: false, entries };
      });
      if (gridChanged) announceRefresh();
      refreshThread();
      refreshHistory();
    }
  }, [addEntry, patch, refreshHistory, refreshThread]);

  /** Pick an Auto Tend run up: a thread of yours that carries its transcript, so the model keeps what it read. */
  const pickUp = useCallback(async (autoThreadId: number) => {
    const t = await agentApi.forkThread(autoThreadId);
    await loadThreadDetail(t.id, false);
    refreshHistory();
    return t;
  }, [loadThreadDetail, refreshHistory]);

  const send = useCallback(async (text: string) => {
    const slug = slugRef.current;
    const trimmed = text.trim();
    const current = stateRef.current;
    if (!slug || !trimmed || current.busy) return;
    let thread = current.thread;
    if (!thread || current.readOnly || thread.kind === 'auto') {
      try {
        if (thread && thread.kind === 'auto') {
          // An Auto run is read-only: pick it up into a thread of yours that carries its transcript, then post there.
          thread = await pickUp(thread.id);
        } else {
          // Continue an older thread of yours, or the brand's active one.
          thread = current.readOnly && current.thread ? current.thread : await agentApi.activeThread(slug);
        }
      } catch (e) { patch({ lastError: friendly(e) }); return; }
    }
    patch({ thread });
    addEntry({ kind: 'user', id: eid(), text: trimmed });
    await runTurn(thread.id, { text: trimmed });
  }, [addEntry, patch, runTurn, pickUp]);

  const stop = useCallback(() => { abortRef.current?.abort(); }, []);

  const confirm = useCallback(async (id: number) => {
    try {
      const d = await agentApi.confirmDraft(id);
      patch((s) => ({ drafts: { ...s.drafts, [d.id]: d }, thread: s.thread ? { ...s.thread, pending_count: Math.max(0, s.thread.pending_count - 1) } : s.thread }));
      announceRefresh();
    } catch (e) { patch({ lastError: friendly(e) }); }
  }, [patch]);

  const skip = useCallback(async (id: number) => {
    try {
      const d = await agentApi.skipDraft(id);
      patch((s) => ({ drafts: { ...s.drafts, [d.id]: d }, thread: s.thread ? { ...s.thread, pending_count: Math.max(0, s.thread.pending_count - 1) } : s.thread }));
    } catch (e) { patch({ lastError: friendly(e) }); }
  }, [patch]);

  const renderAgain = useCallback(async (id: number) => {
    try {
      const d = await agentApi.renderDraft(id);
      patch((s) => ({ drafts: { ...s.drafts, [d.id]: d } }));
    } catch (e) { patch({ lastError: friendly(e) }); }
  }, [patch]);

  const confirmAllWith = useCallback(async (stopAsking: boolean) => {
    const id = stateRef.current.thread?.id;
    if (!id) return;
    try {
      const out = await agentApi.confirmAll(id, stopAsking);
      patch((s) => {
        const drafts = { ...s.drafts };
        out.confirmed.forEach((d) => { drafts[d.id] = d; });
        return { drafts, thread: s.thread ? { ...s.thread, pending_count: out.pending_count, auto_confirm: out.auto_confirm } : s.thread };
      });
      if (stopAsking) addNote('Confirmed. Later drafts in this thread land in Tended on their own.');
      announceRefresh();
    } catch (e) { patch({ lastError: friendly(e) }); }
  }, [addNote, patch]);

  const confirmAll = useCallback(() => confirmAllWith(false), [confirmAllWith]);
  const confirmAllStopAsking = useCallback(() => confirmAllWith(true), [confirmAllWith]);

  const setAutoConfirm = useCallback(async (on: boolean) => {
    const id = stateRef.current.thread?.id;
    if (!id) return;
    try {
      const t = await agentApi.setAutoConfirm(id, on);
      patch((s) => ({ thread: s.thread ? { ...s.thread, auto_confirm: t.auto_confirm } : t }));
      addNote(on ? 'Later drafts in this thread land in Tended on their own.' : 'Back to confirming each draft here.');
    } catch (e) { patch({ lastError: friendly(e) }); }
  }, [addNote, patch]);

  const skipAll = useCallback(async () => {
    const id = stateRef.current.thread?.id;
    if (!id) return;
    try {
      const out = await agentApi.skipAll(id);
      patch((s) => { const drafts = { ...s.drafts }; out.skipped.forEach((d) => { drafts[d.id] = d; }); return { drafts, thread: s.thread ? { ...s.thread, pending_count: out.pending_count } : s.thread }; });
    } catch (e) { patch({ lastError: friendly(e) }); }
  }, [patch]);

  const decideAction = useCallback(async (id: number, approve: boolean, stopAsking = false) => {
    const current = stateRef.current;
    if (current.busy) return;
    try {
      const out = approve ? await agentApi.approveAction(id, stopAsking) : await agentApi.skipAction(id);
      patch((s) => ({ actions: { ...s.actions, [out.action.id]: out.action }, thread: out.thread }));
      addNote(out.message);
      if (approve && out.action.kind === 'enqueue') announceRefresh();
      if (approve && out.action.kind === 'colors') {
        // Design ▸ Colors and every picker read the palette cache; the card just changed it.
        const bid = out.action.payload?.brand_id;
        if (typeof bid === 'number') loadBrandPalette(bid, true);
        window.dispatchEvent(new CustomEvent(BRAND_COLORS_CHANGED_EVENT, { detail: { brandId: bid } }));
      }
      if (out.resume && current.thread) await runTurn(current.thread.id, { resume: true });
    } catch (e) { patch({ lastError: friendly(e) }); }
  }, [addNote, patch, runTurn]);

  const approveAction = useCallback((id: number, stopAsking = false) => decideAction(id, true, stopAsking), [decideAction]);
  const skipAction = useCallback((id: number) => decideAction(id, false), [decideAction]);

  const revise = useCallback((id: number) => {
    const d = stateRef.current.drafts[id];
    const label = d ? `the ${d.platform} draft (item ${id})` : `item ${id}`;
    patch({ composerSeed: `Revise ${label}: ` });
  }, [patch]);

  const newThread = useCallback(async () => {
    const slug = slugRef.current;
    if (!slug) return;
    try {
      const t = await agentApi.newThread(slug);
      patch({ thread: t, entries: [{ kind: 'note', id: eid(), text: 'New thread. The last one is in history; a thread also rolls over on its own after idle hours.' }], drafts: {}, actions: {}, readOnly: false, historyOpen: false });
      refreshHistory();
    } catch (e) { patch({ lastError: friendly(e) }); }
  }, [patch, refreshHistory]);

  const viewThread = useCallback(async (id: number) => {
    try { await loadThreadDetail(id, id !== stateRef.current.thread?.id); } catch (e) { patch({ lastError: friendly(e) }); }
  }, [loadThreadDetail, patch]);

  const continueThread = useCallback(() => {
    const t = stateRef.current.thread;
    if (t?.kind === 'auto') { pickUp(t.id).catch((e) => patch({ lastError: friendly(e) })); return; }
    patch({ readOnly: false });
  }, [patch, pickUp]);

  const refreshDrafts = useCallback(async () => { await refreshThread(); }, [refreshThread]);

  const setSpeed = useCallback(async (speed: AgentSpeed) => {
    const slug = slugRef.current;
    if (!slug) return;
    patch({ speed, effectiveSpeed: speed });
    try {
      const prefs = await agentApi.patchPrefs(slug, { speed });
      patch({ speed: prefs.speed, effectiveSpeed: prefs.effective_speed });
    } catch (e) { patch({ lastError: friendly(e) }); }
  }, [patch]);

  const setCols = useCallback((cols: number) => {
    const slug = slugRef.current;
    const n = Math.max(AGENT_MIN_COLS, Math.min(AGENT_MAX_COLS, Math.round(cols)));
    patch({ cols: n });
    if (!slug) return;
    try { localStorage.setItem(colsKey(slug), String(n)); } catch (e) { /* ignore */ }
    agentApi.patchPrefs(slug, { panel_cols: n }).catch(() => { /* local copy is enough */ });
  }, [patch]);

  useEffect(() => {
    if (state.historyOpen) refreshHistory();
  }, [state.historyOpen, refreshHistory]);

  const value = useMemo<AgentContextType>(() => ({
    ...state,
    loadBrand, openPanel, closePanel, togglePanel, send, stop, confirm, skip, confirmAll, confirmAllStopAsking, setAutoConfirm,
    skipAll, revise, seedComposer, newThread, openHistory, viewThread, continueThread, refreshDrafts, renderAgain,
    approveAction, skipAction, setSpeed, setCols, setPageContext, addNote,
  }), [state, loadBrand, openPanel, closePanel, togglePanel, send, stop, confirm, skip, confirmAll, confirmAllStopAsking,
       setAutoConfirm, skipAll, revise, seedComposer, newThread, openHistory, viewThread, continueThread, refreshDrafts,
       renderAgain, approveAction, skipAction, setSpeed, setCols, setPageContext, addNote]);

  return <AgentContext.Provider value={value}>{children}</AgentContext.Provider>;
};

export default AgentContext;
